/** Calculates raw, period-based statistics for one recurring Activity. */
import type { Completion, Period, RecurringActivity } from '@life-rpg/shared';

export interface ActivityStats {
  readonly lifetimeCount: number;
  readonly currentPeriodCount: number;
  readonly completionRate28Days: number;
}

const startOfDay = (date: Date): Date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

const EXPECTED_BASE_PERIODS_IN_28_DAYS: Record<Period, number> = {
  day: 28,
  week: 4,
  month: 1,
};

const parseLocalDate = (date: string): Date => {
  const [year, month, day] = date.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error('Activity target has an invalid start date.');
  }
  return new Date(year, month - 1, day);
};

const calendarDayNumber = (date: Date): number =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;

const monthBoundary = (anchor: Date, monthOffset: number, dayOfMonth: number): Date => {
  const year = anchor.getFullYear();
  const month = anchor.getMonth() + monthOffset;
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(dayOfMonth, lastDay));
};

const currentPeriodStartsAt = (target: RecurringActivity['target'], now: Date): Date | null => {
  const anchor = parseLocalDate(target.startsOn);

  if (target.per === 'month') {
    const firstBoundary = monthBoundary(anchor, 0, target.dayOfMonth ?? anchor.getDate());
    const monthsSinceAnchor =
      (now.getFullYear() - anchor.getFullYear()) * 12 + now.getMonth() - anchor.getMonth();
    let periodIndex = Math.floor(monthsSinceAnchor / target.every);
    let boundary = monthBoundary(
      anchor,
      periodIndex * target.every,
      target.dayOfMonth ?? anchor.getDate(),
    );
    if (boundary.getTime() > now.getTime()) {
      periodIndex -= 1;
      boundary = monthBoundary(
        anchor,
        periodIndex * target.every,
        target.dayOfMonth ?? anchor.getDate(),
      );
    }
    return periodIndex < 0 || now.getTime() < firstBoundary.getTime() ? null : boundary;
  }

  const daysPerBasePeriod = target.per === 'day' ? 1 : 7;
  const elapsedDays = calendarDayNumber(now) - calendarDayNumber(anchor);
  if (elapsedDays < 0) return null;
  const periodDays = daysPerBasePeriod * target.every;
  const periodStart = new Date(anchor);
  periodStart.setDate(periodStart.getDate() + Math.floor(elapsedDays / periodDays) * periodDays);
  return periodStart;
};

const startOf28DayWindow = (now: Date): Date => {
  const start = startOfDay(now);
  start.setDate(start.getDate() - 27);
  return start;
};

const sumSince = (completions: readonly Completion[], since: Date): number =>
  completions
    .filter((completion) => new Date(completion.at).getTime() >= since.getTime())
    .reduce((total, completion) => total + completion.qty, 0);

export const calculateActivityStats = (
  activity: RecurringActivity,
  completions: readonly Completion[],
  now: Date,
): ActivityStats => {
  const nowTime = now.getTime();
  const activityCompletions = completions.filter(
    (completion) =>
      completion.activityId === activity.id && new Date(completion.at).getTime() <= nowTime,
  );
  const count28Days = sumSince(activityCompletions, startOf28DayWindow(now));
  const currentPeriodStart = currentPeriodStartsAt(activity.target, now);
  const expected28Days =
    (activity.target.qty * EXPECTED_BASE_PERIODS_IN_28_DAYS[activity.target.per]) /
    activity.target.every;

  return {
    lifetimeCount: activityCompletions.reduce((total, completion) => total + completion.qty, 0),
    currentPeriodCount:
      currentPeriodStart === null ? 0 : sumSince(activityCompletions, currentPeriodStart),
    completionRate28Days: count28Days / expected28Days,
  };
};
